import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  XCircle,
  Copy,
  RefreshCw,
  Server,
  Globe,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface DNSRecord {
  id: string;
  type: 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT' | 'NS';
  name: string;
  value: string;
  ttl: number;
  priority?: number;
  required: boolean;
  description: string;
}

interface NameserverStatus {
  name: string;
  status: 'checking' | 'verified' | 'failed' | 'pending';
  responseTime?: number;
}

interface RecordStatus {
  recordId: string;
  status: 'checking' | 'verified' | 'failed' | 'pending';
  progress: number;
  lastChecked: Date | null;
  nameservers: NameserverStatus[];
  error?: string;
}

interface DNSVerificationTrackerProps {
  domainId: string;
  dnsRecords: DNSRecord[];
  onStatusChange?: (recordId: string, status: RecordStatus) => void;
}

const NAMESERVERS = [
  'ns1.cloudflare.com',
  'ns2.cloudflare.com',
  'a.root-servers.net',
  'b.root-servers.net'
];

const DNS_PROVIDERS = [
  {
    name: 'Cloudflare',
    steps: [
      'Log in to your Cloudflare dashboard',
      'Select your domain',
      'Go to DNS → Records',
      'Click "Add record"',
      'Enter the record details and save'
    ]
  },
  {
    name: 'GoDaddy',
    steps: [
      'Sign in to your GoDaddy account',
      'Go to Domain Manager',
      'Click DNS next to your domain',
      'Click "Add" to create a new record',
      'Select record type and enter details'
    ]
  },
  {
    name: 'Namecheap',
    steps: [
      'Log in to your Namecheap account',
      'Go to Domain List',
      'Click "Manage" next to your domain',
      'Go to Advanced DNS tab',
      'Click "Add New Record"'
    ]
  }
];

const TROUBLESHOOTING_TIPS = [
  'DNS propagation can take up to 48 hours globally',
  'Clear your local DNS cache: flush DNS on your system',
  'Check if you have the correct record type selected',
  'Verify the record name includes or excludes the domain as needed',
  'Ensure TTL values are reasonable (300-3600 seconds)',
  'Some providers require a trailing dot for CNAME values'
];

export default function DNSVerificationTracker({
  domainId,
  dnsRecords,
  onStatusChange
}: DNSVerificationTrackerProps) {
  const [recordStatuses, setRecordStatuses] = useState<Record<string, RecordStatus>>({});
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  // Initialize record statuses
  useEffect(() => {
    const initialStatuses: Record<string, RecordStatus> = {};
    dnsRecords.forEach(record => {
      initialStatuses[record.id] = {
        recordId: record.id,
        status: 'pending',
        progress: 0,
        lastChecked: null,
        nameservers: NAMESERVERS.map(ns => ({
          name: ns,
          status: 'pending'
        }))
      };
    });
    setRecordStatuses(initialStatuses);
  }, [dnsRecords]);

  // Simulate DNS checking for a single record
  const checkRecord = useCallback(async (recordId: string) => {
    const record = dnsRecords.find(r => r.id === recordId);
    if (!record) return;

    // Update status to checking
    setRecordStatuses(prev => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        status: 'checking',
        progress: 0,
        nameservers: prev[recordId].nameservers.map(ns => ({
          ...ns,
          status: 'checking'
        }))
      }
    }));

    // Simulate checking each nameserver
    for (let i = 0; i < NAMESERVERS.length; i++) {
      const nameserver = NAMESERVERS[i];

      // Random delay to simulate network latency
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

      // Simulate response time
      const responseTime = Math.floor(50 + Math.random() * 200);

      // Simulate success/failure (90% success rate)
      const success = Math.random() > 0.1;

      setRecordStatuses(prev => ({
        ...prev,
        [recordId]: {
          ...prev[recordId],
          progress: ((i + 1) / NAMESERVERS.length) * 100,
          nameservers: prev[recordId].nameservers.map((ns, idx) =>
            idx === i
              ? { ...ns, status: success ? 'verified' : 'failed', responseTime }
              : ns
          )
        }
      }));
    }

    // Final status based on nameserver results
    const finalStatus = recordStatuses[recordId]?.nameservers.every(ns => ns.status === 'verified')
      ? 'verified'
      : recordStatuses[recordId]?.nameservers.some(ns => ns.status === 'verified')
      ? 'verified'
      : 'failed';

    setRecordStatuses(prev => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        status: finalStatus,
        lastChecked: new Date(),
        error: finalStatus === 'failed' ? 'DNS record not found or incorrect value' : undefined
      }
    }));

    if (onStatusChange) {
      onStatusChange(recordId, recordStatuses[recordId]);
    }
  }, [dnsRecords, recordStatuses, onStatusChange]);

  // Check all records
  const checkAllRecords = useCallback(async () => {
    setIsCheckingAll(true);
    const promises = dnsRecords.map(record => checkRecord(record.id));
    await Promise.all(promises);
    setIsCheckingAll(false);
  }, [dnsRecords, checkRecord]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedValue(text);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'text-green-400';
      case 'checking': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'checking': return <RefreshCw className="w-5 h-5 text-yellow-400 animate-spin" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-900 text-white rounded-lg">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2 flex items-center">
          <Globe className="w-8 h-8 mr-3 text-blue-400" />
          DNS Verification Tracker
        </h2>
        <p className="text-gray-400">
          Real-time DNS propagation monitoring for domain: <span className="text-blue-400 font-mono">{domainId}</span>
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-wrap gap-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={checkAllRecords}
          disabled={isCheckingAll}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg font-medium flex items-center"
        >
          <RefreshCw className={`w-5 h-5 mr-2 ${isCheckingAll ? 'animate-spin' : ''}`} />
          {isCheckingAll ? 'Checking All Records...' : 'Check All Records'}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowInstructions(!showInstructions)}
          className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium flex items-center"
        >
          <Info className="w-5 h-5 mr-2" />
          Setup Instructions
          {showInstructions ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
        </motion.button>
      </div>

      {/* Setup Instructions */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 bg-gray-800 rounded-lg p-6"
          >
            <h3 className="text-xl font-semibold mb-4">DNS Provider Setup Instructions</h3>

            {/* Provider Tabs */}
            <div className="flex mb-4 bg-gray-700 rounded-lg p-1">
              {DNS_PROVIDERS.map((provider, index) => (
                <button
                  key={provider.name}
                  onClick={() => setSelectedProvider(index)}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                    selectedProvider === index
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {provider.name}
                </button>
              ))}
            </div>

            {/* Provider Steps */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">{DNS_PROVIDERS[selectedProvider].name} Setup:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                {DNS_PROVIDERS[selectedProvider].steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>

            {/* Troubleshooting */}
            <div>
              <h4 className="font-medium mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-yellow-400" />
                Troubleshooting Tips
              </h4>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                {TROUBLESHOOTING_TIPS.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DNS Records Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Type</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Name</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Value</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">TTL</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Progress</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {dnsRecords.map((record) => {
                const status = recordStatuses[record.id];
                return (
                  <motion.tr
                    key={record.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-750"
                  >
                    {/* Status */}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getStatusIcon(status?.status || 'pending')}
                        <span className={`ml-2 text-sm font-medium ${getStatusColor(status?.status || 'pending')}`}>
                          {status?.status === 'checking' ? 'Checking...' :
                           status?.status === 'verified' ? 'Verified' :
                           status?.status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </div>
                      {status?.lastChecked && (
                        <div className="text-xs text-gray-400 mt-1">
                          Last: {status.lastChecked.toLocaleTimeString()}
                        </div>
                      )}
                    </td>

                    {/* Type */}
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-900 text-blue-200 rounded text-sm font-mono">
                        {record.type}
                      </span>
                      {record.priority && (
                        <div className="text-xs text-gray-400 mt-1">
                          Priority: {record.priority}
                        </div>
                      )}
                    </td>

                    {/* Name */}
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm">{record.name}</div>
                      <div className="text-xs text-gray-400">{record.description}</div>
                    </td>

                    {/* Value */}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="font-mono text-sm break-all mr-2">
                          {record.value.length > 50 ? `${record.value.substring(0, 50)}...` : record.value}
                        </span>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => copyToClipboard(record.value)}
                          className="p-1 hover:bg-gray-600 rounded"
                          title="Copy value"
                        >
                          <Copy className="w-4 h-4 text-gray-400" />
                        </motion.button>
                      </div>
                      {copiedValue === record.value && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-xs text-green-400 mt-1"
                        >
                          Copied!
                        </motion.div>
                      )}
                    </td>

                    {/* TTL */}
                    <td className="px-6 py-4">
                      <span className="text-sm">{record.ttl}s</span>
                    </td>

                    {/* Progress */}
                    <td className="px-6 py-4">
                      <div className="w-24">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>Progress</span>
                          <span>{Math.round(status?.progress || 0)}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <motion.div
                            className="bg-blue-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${status?.progress || 0}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => checkRecord(record.id)}
                        disabled={status?.status === 'checking'}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 rounded text-sm flex items-center"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${status?.status === 'checking' ? 'animate-spin' : ''}`} />
                        Check
                      </motion.button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nameserver Status */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {NAMESERVERS.map((nameserver) => {
          const allStatuses = Object.values(recordStatuses).flatMap(status =>
            status.nameservers.filter(ns => ns.name === nameserver)
          );
          const verified = allStatuses.filter(ns => ns.status === 'verified').length;
          const total = allStatuses.length;
          const checking = allStatuses.some(ns => ns.status === 'checking');

          return (
            <motion.div
              key={nameserver}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <Server className="w-5 h-5 text-blue-400" />
                {checking && <RefreshCw className="w-4 h-4 text-yellow-400 animate-spin" />}
              </div>
              <h3 className="font-medium text-sm mb-1">{nameserver}</h3>
              <div className="text-xs text-gray-400 mb-2">
                {verified}/{total} verified
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <motion.div
                  className="bg-green-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: total > 0 ? `${(verified / total) * 100}%` : '0%' }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-green-400">
                {Object.values(recordStatuses).filter(s => s.status === 'verified').length}
              </h3>
              <p className="text-sm text-gray-400">Verified Records</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-yellow-400">
                {Object.values(recordStatuses).filter(s => s.status === 'checking').length}
              </h3>
              <p className="text-sm text-gray-400">Checking</p>
            </div>
            <RefreshCw className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-red-400">
                {Object.values(recordStatuses).filter(s => s.status === 'failed').length}
              </h3>
              <p className="text-sm text-gray-400">Failed</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Propagation Info */}
      <div className="mt-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-300 mb-2">DNS Propagation Information</h3>
            <p className="text-sm text-gray-300 mb-2">
              DNS changes can take anywhere from a few minutes to 48 hours to propagate globally.
              This tool simulates real-time checking across multiple nameservers to give you an
              accurate picture of your DNS record status.
            </p>
            <p className="text-sm text-gray-400">
              Estimated propagation time: <strong>5-30 minutes</strong> for most changes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}